/**
 * SamLogo — Reusable SAM Surveyor logo mark + optional wordmark
 *
 * Three equal arc segments: Blue (#5B9FFF), Yellow (#FFC107), hidden (background).
 * Stroke-width adapts for legibility at small sizes per logo_spec.html.
 *
 * Props:
 *   size        — pixel width/height of the mark (default 32)
 *   showWordmark — show "SAM SURVEYOR" text beside the mark (default false)
 *   variant     — 'light' | 'dark' | 'onWhite' controls text + hidden arc color
 *   className   — optional CSS class on the wrapper
 *   style       — optional inline style on the wrapper
 */

import React from 'react';

export default function SamLogo({
    size = 32,
    showWordmark = false,
    variant = 'onWhite',
    className,
    style,
}) {
    // Stroke width adapts per logo spec: thicker at small sizes
    let strokeWidth = 7;
    if (size <= 16) strokeWidth = 9;
    else if (size <= 24) strokeWidth = 8;
    else if (size >= 96) strokeWidth = 6;

    // Hidden arc color & text color depend on variant
    const hiddenArcColor =
        variant === 'light'
            ? '#1e3a5f'   // dark header bg
            : variant === 'dark'
            ? '#111111'
            : 'var(--color-bg-base)';  // onWhite — matches app bg

    const textColor =
        variant === 'light'
            ? '#FFFFFF'
            : variant === 'dark'
            ? '#FFFFFF'
            : '#3C4043';  // neutral-700

    // Wordmark font size scales with mark size
    const wordmarkSize = Math.max(12, Math.round(size * 0.45));

    return (
        <div
            className={className}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: Math.round(size * 0.3),
                ...style,
            }}
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 100 100"
                aria-label="Sam Surveyor logo"
                role="img"
            >
                {/* Blue arc — top-left */}
                <circle
                    cx="50" cy="50" r="34"
                    fill="none"
                    stroke="#5B9FFF"
                    strokeWidth={strokeWidth}
                    strokeDasharray="58 155.6"
                    strokeDashoffset="55"
                    strokeLinecap="round"
                />
                {/* Yellow arc — bottom */}
                <circle
                    cx="50" cy="50" r="34"
                    fill="none"
                    stroke="#FFC107"
                    strokeWidth={strokeWidth}
                    strokeDasharray="58 155.6"
                    strokeDashoffset="-16"
                    strokeLinecap="round"
                />
                {/* Hidden arc — right (matches background) */}
                <circle
                    cx="50" cy="50" r="34"
                    fill="none"
                    stroke={hiddenArcColor}
                    strokeWidth={strokeWidth}
                    strokeDasharray="58 155.6"
                    strokeDashoffset="-87"
                    strokeLinecap="round"
                />
            </svg>

            {showWordmark && (
                <span
                    style={{
                        fontWeight: 300,
                        letterSpacing: '4px',
                        textTransform: 'uppercase',
                        fontSize: wordmarkSize,
                        color: textColor,
                        whiteSpace: 'nowrap',
                        fontFamily:
                            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    }}
                >
                    Sam Surveyor
                </span>
            )}
        </div>
    );
}
